import { useEffect } from "react";
import { CircleCheck, SquareArrowOutUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { BillingCadence } from "@intelligems/ig-types/dtos/billing/commands/catalog";
import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgDialog } from "@intelligems/ig-theme/IgDialog";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { Box, Stack, useTheme } from "@mui/material";
import { useCreateUsageBillingUrl } from "@src/api/shopify-app-subscriptions/create-usage-billing-url";
import { useDialog } from "@src/hooks/dialog";
import { SETTINGS_ROUTES } from "@src/routes/routes";
import { SettingsSectionId } from "@src/types/navigation";
import { toAnalyticsBillingCadence } from "@src/utils/billing-plan-intent";
import {
  mpBillingPlanApprovalModalViewed,
  mpBillingPlanSelected,
} from "@src/utils/mixpanel";

// `billing_plan_selected` and `billing_plan_approval_modal_viewed` predate the
// four-value cadence vocabulary and their reports still read this two-value
// split, so it stays exactly as it was. New events use the canonical converter.
const toLegacyBillingCadence = (interval: string): BillingCadence =>
  interval === "year" ? "annual" : "monthly";

export const ApprovePlanConfirmationDialog = () => {
  const dialog = useDialog("approvePlanConfirmation");
  const navigate = useNavigate();
  const theme = useTheme();
  const createUsageBillingUrl = useCreateUsageBillingUrl();

  const planName = dialog.props?.planName ?? "";
  const monthlyPrice = dialog.props?.monthlyPrice ?? 0;
  const interval = dialog.props?.interval ?? "month";
  const features = dialog.props?.features ?? [];
  const billingCycle = toLegacyBillingCadence(interval);
  const approvalCadence = toAnalyticsBillingCadence(
    dialog.props?.interval,
    dialog.props?.intervalCount
  );

  useEffect(() => {
    if (dialog.isOpen) {
      mpBillingPlanApprovalModalViewed({ billingCycle, planName });
    }
  }, [dialog.isOpen, billingCycle, planName]);

  const formattedPrice = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(monthlyPrice);

  const handleConfirm = () => {
    mpBillingPlanSelected({ billingCycle, planName });
    if (dialog.props?.subscriptionLinkId) {
      createUsageBillingUrl.mutate({
        body: { subscriptionLinkId: dialog.props.subscriptionLinkId },
        queryParams: undefined,
        pathParams: undefined,
        analytics: {
          source: "legacy_plan_dialog",
          billingCycle: approvalCadence,
          planName,
        },
      });
    }
    dialog.close();
  };

  const handleViewAllPlans = () => {
    dialog.close();
    void navigate(`${SETTINGS_ROUTES.Settings}#${SettingsSectionId.Billing}`);
  };

  return (
    <IgDialog
      title="Approve plan in Shopify"
      showDialog={dialog.isOpen}
      handleClose={dialog.close}
      showCloseIcon
      hideActions
      rawContent={
        <Stack spacing={3} sx={{ position: "relative", top: -14 }}>
          <Box
            sx={{
              backgroundColor: theme.palette.grey[300],
              display: "flex",
              justifyContent: "center",
              py: 6,
            }}
          >
            <Box
              sx={{
                width: 350,
                backgroundColor: "white",
                border: `1.5px solid ${theme.palette.info.main}`,
                borderRadius: "8px",
                px: 5,
                py: 6,
              }}
            >
              <Stack spacing={4}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <IgTypography
                    variant="h2"
                    fontWeight={600}
                    color="text.secondary"
                  >
                    {planName}
                  </IgTypography>
                  <IgTypography variant="body1" color="text.secondary">
                    {formattedPrice}/{interval}
                  </IgTypography>
                </Stack>
                {features.length > 0 && (
                  <Stack spacing={3}>
                    {features.map((feature) => (
                      <Stack
                        key={feature}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <CircleCheck
                          size={18}
                          color={theme.igIconColors.accent}
                        />
                        <IgTypography variant="body2" color="text.secondary">
                          {feature}
                        </IgTypography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Box>
          </Box>
          <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
            <IgButton
              variant="outlined"
              color="secondary"
              onClick={handleViewAllPlans}
              startIcon={<SquareArrowOutUpRight size={16} />}
            >
              View all plans
            </IgButton>
            <IgButton variant="contained" onClick={handleConfirm}>
              Approve in Shopify
            </IgButton>
          </Stack>
        </Stack>
      }
    />
  );
};
